import cn from 'classnames'
import type { ReactNode } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Tooltip } from 'core-components/library/tooltip'
import { getQualityBand, getQualityBandColor } from 'partner360/components/qualityIndicator'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import { formatCockpitScore, toBadgeScore } from '../../../lib'

import styles from './PlanQualityBadge.module.scss'

interface IPlanQualityBadgeProps {
  score: number | null
  small?: boolean
  pill?: boolean
  displayValue?: ReactNode
  title?: string
  onClick?: () => void
}

export const PlanQualityBadge = ({
  score,
  small = false,
  pill = false,
  displayValue,
  title,
  onClick,
}: IPlanQualityBadgeProps) => {
  /** The one case that shows nothing at all rather than a number: the `small` adornment sits
   *  beside a label it would otherwise clutter with a score no one entered. */
  if (small && score === null) {
    return null
  }

  /** A pill always reads a number — a score with nothing behind it is the 0 it amounts to, never
   *  the `--` value cells use. Band and text both come off that, so it wears a real band color. */
  const value = toBadgeScore(score)
  const band = getQualityBand(value)
  const content = displayValue ?? formatCockpitScore(value)
  const commonProps = {
    className: cn(styles.badge, { [styles.small]: small, [styles.pill]: pill }),
    style: { backgroundColor: getQualityBandColor(band) },
    title,
  }
  let badge: ReactNode

  if (onClick) {
    badge = (
      <button type={'button'} {...commonProps} aria-label={title} onClick={onClick}>
        {content}
      </button>
    )
  } else {
    badge = <span {...commonProps}>{content}</span>
  }

  if (!small) {
    return badge
  }

  return (
    <Tooltip
      tipContent={() => (
        <Translation text={FINANCIAL_COCKPIT_TRANSLATION_KEYS.planQuality.combinedTitle} span />
      )}
    >
      {badge}
    </Tooltip>
  )
}

PlanQualityBadge.displayName = 'PlanQualityBadge'
